import { motion } from 'motion/react'
import { useInView } from 'motion/react'
import { useRef } from 'react'
import { fontSans, fontMono, hexToRgba } from './theme'

const kernelLayers = [
  { name: 'Connectors', color: '#4A90E2' },
  { name: 'Catalog & Profiling', color: '#4A90E2' },
  { name: 'PII & Policy Engine', color: '#2DD4BF' },
  { name: 'Quality & Lineage', color: '#F5B850' },
  { name: 'Durable Execution', color: '#9D4EDD' },
  { name: 'Workspaces & Audit', color: '#4A90E2' },
]

const doors = [
  {
    name: 'SynKrasis Platform',
    note: 'data teams',
    color: '#4A90E2',
    href: '#platform',
  },
  {
    name: 'Ontext',
    note: 'AI agents, via MCP',
    color: '#9D4EDD',
    href: '#ontext',
  },
  {
    name: 'Sentinel',
    note: 'privacy & compliance',
    color: '#2DD4BF',
    href: '#sentinel',
  },
]

export function Kernel() {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section
      id="kernel"
      style={{
        padding: '7rem 2rem',
        background: 'linear-gradient(180deg, #0A0F1F 0%, #0c1222 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background:
            'linear-gradient(90deg, transparent, rgba(157,78,221,0.3), rgba(45,212,191,0.3), transparent)',
        }}
      />

      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <span
              style={{
                fontFamily: fontSans,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#4A90E2',
                display: 'block',
                marginBottom: '1rem',
              }}
            >
              Better Together
            </span>
            <h2
              style={{
                fontFamily: fontSans,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 800,
                color: '#ffffff',
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
                marginBottom: '1.25rem',
              }}
            >
              Three front doors.{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, #4A90E2, #9D4EDD, #2DD4BF)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                One engine underneath.
              </span>
            </h2>
            <p
              style={{
                fontFamily: fontSans,
                fontSize: '1.05rem',
                color: 'rgba(255,255,255,0.5)',
                maxWidth: '620px',
                margin: '0 auto',
                lineHeight: 1.7,
              }}
            >
              Start with the product that solves today&apos;s pain. Add the
              others when that pain arrives — same kernel, same policies, same
              audit trail. No migration, no rip-and-replace.
            </p>
          </motion.div>
        </div>

        {/* Three doors */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
          }}
        >
          {doors.map((d, i) => (
            <motion.div
              key={d.name}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              style={{
                textAlign: 'center',
                padding: '1.1rem 1rem',
                borderRadius: '14px',
                background: hexToRgba(d.color, 0.06),
                border: `1px solid ${hexToRgba(d.color, 0.25)}`,
              }}
            >
              <div
                style={{
                  fontFamily: fontSans,
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  color: '#ffffff',
                }}
              >
                {d.name}
              </div>
              <div
                style={{
                  fontFamily: fontSans,
                  fontSize: '0.74rem',
                  color: hexToRgba(d.color, 0.9),
                  marginTop: '2px',
                }}
              >
                {d.note}
              </div>
            </motion.div>
          ))}
        </div>

        {/* funnel arrows */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.35 }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '0.5rem 0',
          }}
        >
          <svg width="24" height="28" viewBox="0 0 24 28" fill="none">
            <path
              d="M12 0v20"
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="1.4"
              strokeDasharray="3 3"
            />
            <path
              d="M6 17.5 12 26l6-8.5"
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.div>

        {/* Kernel */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          style={{
            borderRadius: '20px',
            border: '1px solid rgba(255,255,255,0.1)',
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.015))',
            padding: '1.75rem',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* animated sheen */}
          <div
            className="kernel-sheen"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background:
                'linear-gradient(90deg, transparent, rgba(74,144,226,0.8), rgba(157,78,221,0.8), rgba(45,212,191,0.8), transparent)',
            }}
          />
          <div
            style={{
              fontFamily: fontMono,
              fontSize: '0.72rem',
              letterSpacing: '0.14em',
              color: 'rgba(255,255,255,0.45)',
              textTransform: 'uppercase',
              marginBottom: '1.25rem',
            }}
          >
            The SynKrasis Governance Kernel
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {kernelLayers.map((l, i) => (
              <motion.span
                key={l.name}
                initial={{ opacity: 0, y: 10 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.45, delay: 0.5 + i * 0.08 }}
                style={{
                  fontFamily: fontSans,
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.85)',
                  padding: '9px 16px',
                  borderRadius: '10px',
                  background: hexToRgba(l.color, 0.09),
                  border: `1px solid ${hexToRgba(l.color, 0.28)}`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: l.color,
                  }}
                />
                {l.name}
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* outcome */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.75 }}
          style={{
            marginTop: '1.25rem',
            textAlign: 'center',
            padding: '1.25rem 1.75rem',
            borderRadius: '16px',
            background:
              'linear-gradient(135deg, rgba(74,144,226,0.08), rgba(157,78,221,0.08), rgba(45,212,191,0.08))',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <span
            style={{
              fontFamily: fontSans,
              fontSize: '1rem',
              fontWeight: 600,
              color: 'rgba(255,255,255,0.9)',
            }}
          >
            Governed, agent-ready data
          </span>
          <span
            style={{
              fontFamily: fontSans,
              fontSize: '0.9rem',
              color: 'rgba(255,255,255,0.5)',
              display: 'block',
              marginTop: '4px',
            }}
          >
            every read masked per policy · every action audited · every product
            speaking the same language
          </span>
        </motion.div>
      </div>

      <style>{`
        @keyframes kernelSheen {
          0% { opacity: 0.4; }
          50% { opacity: 1; }
          100% { opacity: 0.4; }
        }
        .kernel-sheen {
          animation: kernelSheen 3s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .kernel-sheen { animation: none; }
        }
      `}</style>
    </section>
  )
}
